import { useQueryClient, type Query } from "@tanstack/react-query";
import { getQueryKey } from "@trpc/react-query";
import { SearchTemplatesOutput } from "@zapier/bff";
import { trpc } from "../trpc";
import { useCurrentUser } from "../user/useCurrentUser";

interface ChangeTemplateOwnerInput {
  templateId: string;
  ownerId: string;
  newOwnerFullName: string;
  newOwnerPhotoUrl?: string | null;
}

interface ChangeTemplateOwnerOptions {
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

/** Drops a template from cached search results and syncs common pagination fields until refetch. */
export function removeTemplateFromSearchOutput(
  old: SearchTemplatesOutput | undefined,
  templateId: string,
): SearchTemplatesOutput | undefined {
  if (!old) return old;
  if (!old.docs.some((t) => t.id === templateId)) return old;

  const documentsAfterRemoval = old.docs.filter((t) => t.id !== templateId);
  const totalTemplateCount = Math.max(0, old.totalDocs - 1);
  const limit = Math.max(old.limit, 1);
  const totalPages =
    totalTemplateCount === 0 ? 0 : Math.ceil(totalTemplateCount / limit);

  return {
    ...old,
    docs: documentsAfterRemoval,
    totalDocs: totalTemplateCount,
    totalPages,
    hasNextPage: totalPages > 0 && old.page < totalPages,
    hasPrevPage: old.page > 1,
    // eslint-disable-next-line unicorn/no-null -- SearchTemplatesOutput uses null
    nextPage: totalPages > 0 && old.page < totalPages ? old.page + 1 : null,
    // eslint-disable-next-line unicorn/no-null -- SearchTemplatesOutput uses null
    prevPage: old.page > 1 ? old.page - 1 : null,
  };
}

/**
 * Whether to optimistically remove a row from this `template.search` cache entry after change-owner.
 * Safe targets match `useTemplatesList` URL → `owner` mapping: default list (no filter),
 * "owned by me" (`owner=userId`), "Shared with me" (`owner=-userId`).
 * Other `owner` values (e.g. URL collaborator filter) are skipped — invalidate still reconciles.
 */
function getTemplateSearchInputFromQueryKey(
  queryKey: Query["queryKey"],
): { owner?: string } | undefined {
  if (!Array.isArray(queryKey)) return undefined;
  for (const part of queryKey) {
    if (
      part !== null &&
      typeof part === "object" &&
      "input" in part &&
      (part as { input?: unknown }).input !== undefined
    ) {
      return (part as { input?: { owner?: string } }).input;
    }
  }
  return undefined;
}

export function shouldOptimisticallyRemoveFromTemplateSearchQuery(
  query: Query,
  currentUserId: string | undefined,
): boolean {
  if (currentUserId === undefined) return false;

  const input = getTemplateSearchInputFromQueryKey(query.queryKey);
  if (input === undefined) return false;

  const self = String(currentUserId);
  const rawOwner = input.owner;
  const owner = typeof rawOwner === "string" ? rawOwner.trim() : "";

  // Default / all list — no owner filter (omitted or empty)
  if (owner === "") return true;

  if (owner === self) return true;

  // Shared with me — `shared === "-me"` → `owner=-${userId}`
  if (owner === `-${self}`) return true;

  return false;
}

export function useChangeTemplateOwner() {
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const { user } = useCurrentUser();
  const currentUserId = user?.id === undefined ? undefined : String(user.id);
  const searchKey = getQueryKey(trpc.template.search, undefined, "any");

  const { mutateAsync, isPending } = trpc.template.changeOwner.useMutation({
    onSuccess: () => {
      // Reconcile with server: user may still see the template if they retain access (share/org).
      utils.permission.checkPermissions.invalidate();
      void utils.template.search.invalidate();
    },
    onError: () => {
      void utils.template.search.invalidate();
      void utils.permission.checkPermissions.invalidate();
    },
  });

  const changeTemplateOwner = async (
    input: ChangeTemplateOwnerInput,
    options?: ChangeTemplateOwnerOptions,
  ) => {
    const { templateId, ownerId } = input;

    // Optimistically remove from list — avoids showing a template the user may no longer access.
    // We do not patch owner in-place; a refetch could briefly show stale owner metadata, and the
    // row should disappear for the common "no longer shared" case. Success invalidation reconciles.
    await queryClient.cancelQueries({ queryKey: searchKey });
    queryClient.setQueriesData(
      {
        queryKey: searchKey,
        type: "active",
        predicate: (query) =>
          shouldOptimisticallyRemoveFromTemplateSearchQuery(
            query,
            currentUserId,
          ),
      },
      (old: SearchTemplatesOutput | undefined) =>
        removeTemplateFromSearchOutput(old, templateId),
    );

    try {
      await mutateAsync({ templateId, ownerId });
      options?.onSuccess?.();
    } catch (error) {
      options?.onError?.(error);
    }
  };

  return {
    changeTemplateOwner,
    isPending,
  };
}
